import { Card, Group, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { AnalysisStaff } from '@/api/analyses';
import { useProjectCost } from '@/api/cost';
import type { DataObject } from '@/api/objects';
import type { Report } from '@/api/reports';
import { useFormat } from '@/components/useFormat';
import { STATUS_COLORS, type StatusTone } from '@/theme/colors';
import { brand } from '@/theme/theme';

export const PROJECT_TABS = ['overview', 'objects', 'analyses', 'outputs', 'comments'] as const;
export type ProjectTab = (typeof PROJECT_TABS)[number];

/** Each filter of the staff project page: the tab its table lives on and which rows it keeps. */
export const OBJECT_FILTERS = {
  waiting: (o: DataObject) => o.state === 'pending' || o.state === 'uploading',
  preprocess: (o: DataObject) => o.state === 'verified' || o.state === 'preprocessing',
  ready: (o: DataObject) => o.state === 'ready',
  rejected: (o: DataObject) => o.state === 'rejected',
} as const;
export const ANALYSIS_FILTERS = {
  planned: (a: AnalysisStaff) => a.state === 'planned' || a.state === 'ready',
  running: (a: AnalysisStaff) => a.state === 'running',
  review: (a: AnalysisStaff) => a.state === 'review',
  failed: (a: AnalysisStaff) => a.state === 'failed',
  delivered: (a: AnalysisStaff) => a.state === 'delivered',
} as const;
export const REPORT_FILTERS = {
  visible: (r: Report) => r.visible_to_client,
  hidden: (r: Report) => !r.visible_to_client,
} as const;

export type ObjectFilter = keyof typeof OBJECT_FILTERS;
export type AnalysisFilter = keyof typeof ANALYSIS_FILTERS;
export type ReportFilter = keyof typeof REPORT_FILTERS;
/** `<table>:<filter>`, e.g. `analyses:failed`; the URL carries it. */
export type ProjectFilter = `objects:${ObjectFilter}` | `analyses:${AnalysisFilter}` | `reports:${ReportFilter}`;

export function parseFilter(value: unknown): ProjectFilter | undefined {
  if (typeof value !== 'string') return undefined;
  const [table, key] = value.split(':');
  const known = table === 'objects' ? OBJECT_FILTERS : table === 'analyses' ? ANALYSIS_FILTERS : table === 'reports' ? REPORT_FILTERS : null;
  return known && key in known ? (value as ProjectFilter) : undefined;
}

/** The tab a filter's table lives on. */
export function filterTab(filter: ProjectFilter): ProjectTab {
  const table = filter.split(':')[0];
  return table === 'reports' ? 'outputs' : (table as ProjectTab);
}

/** Rows `filter` keeps when it names `table`; every row otherwise. */
export function applyFilter<T>(rows: T[], table: 'objects' | 'analyses' | 'reports', filter: ProjectFilter | undefined): T[] {
  if (!filter) return rows;
  const [t, key] = filter.split(':');
  if (t !== table) return rows;
  const filters = (table === 'objects' ? OBJECT_FILTERS : table === 'analyses' ? ANALYSIS_FILTERS : REPORT_FILTERS) as unknown as Record<string, (row: T) => boolean>;
  return rows.filter(filters[key]);
}

const TONES: Record<string, StatusTone> = {
  'objects:waiting': 'pending',
  'objects:preprocess': 'active',
  'objects:ready': 'done',
  'objects:rejected': 'failed',
  'analyses:planned': 'muted',
  'analyses:running': 'active',
  'analyses:review': 'pending',
  'analyses:failed': 'failed',
  'analyses:delivered': 'done',
  'reports:visible': 'done',
  'reports:hidden': 'muted',
};

function Chip({ filter, n, active, onPick }: { filter: ProjectFilter; n: number; active: boolean; onPick: (f: ProjectFilter) => void }) {
  const { t } = useTranslation('projects');
  const { number } = useFormat();
  const c = STATUS_COLORS[TONES[filter] ?? 'muted'];
  const label = t(`staff.strip.${filter.replace(':', '.')}`);
  return (
    <UnstyledButton
      onClick={() => onPick(filter)}
      disabled={n === 0}
      aria-pressed={active}
      px={10}
      py={4}
      style={{
        borderRadius: 999,
        border: `1px solid ${n === 0 ? brand.border : c.border}`,
        backgroundColor: active ? c.bg : undefined,
        opacity: n === 0 ? 0.55 : 1,
        cursor: n === 0 ? 'default' : 'pointer',
      }}
    >
      <Text span fz={13} fw={700} c={n === 0 ? brand.textMuted : c.color}>
        {number(n)}
      </Text>{' '}
      <Text span fz={13} c={brand.text}>
        {label}
      </Text>
    </UnstyledButton>
  );
}

/**
 * The project's stored bytes from the latest inventory, a link to the storage card (N19). Before
 * the first inventory, or while it loads, a dash.
 */
function CostChip({ projectId, onPick }: { projectId: string; onPick: () => void }) {
  const { t } = useTranslation('ops');
  const { bytes } = useFormat();
  const cost = useProjectCost(projectId);
  const total = cost.data?.taken_at ? bytes(cost.data.total) : '—';
  const c = STATUS_COLORS.muted;
  return (
    <UnstyledButton
      onClick={onPick}
      aria-label={`${t('cost.title')}: ${total}`}
      px={10}
      py={4}
      style={{ borderRadius: 999, border: `1px solid ${c.border}`, cursor: 'pointer' }}
    >
      <Text span fz={13} fw={700} c={brand.text}>
        {total}
      </Text>
    </UnstyledButton>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Group gap={6} wrap="wrap">
      <Text fz={12} fw={700} tt="uppercase" c={brand.textMuted} style={{ letterSpacing: '0.06em' }} mr={4}>
        {title}
      </Text>
      {children}
    </Group>
  );
}

/**
 * The staff project page's status at a glance (UX review §2.3): objects, analyses and reports
 * counted by what they need, each count a filter on its table; with `projectId` and `onCost`, the
 * project's stored bytes too, a link to its storage card.
 */
export function ProjectStatusStrip({
  objects,
  analyses,
  reports,
  filter,
  onPick,
  projectId,
  onCost,
}: {
  objects: DataObject[];
  analyses: AnalysisStaff[];
  reports: Report[];
  filter: ProjectFilter | undefined;
  onPick: (f: ProjectFilter) => void;
  projectId?: string;
  onCost?: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: to } = useTranslation('ops');
  const live = objects.filter((o) => !o.deleted);
  const chip = <T,>(table: 'objects' | 'analyses' | 'reports', key: string, rows: T[], keep: (row: T) => boolean) => {
    const f = `${table}:${key}` as ProjectFilter;
    return <Chip key={f} filter={f} n={rows.filter(keep).length} active={filter === f} onPick={onPick} />;
  };
  return (
    <Card p="sm">
      <Group gap="xl" wrap="wrap" align="center">
        <Section title={t('staff.strip.section.objects')}>
          {(Object.keys(OBJECT_FILTERS) as ObjectFilter[]).map((k) => chip('objects', k, live, OBJECT_FILTERS[k]))}
        </Section>
        <Section title={t('staff.strip.section.analyses')}>
          {(Object.keys(ANALYSIS_FILTERS) as AnalysisFilter[]).map((k) => chip('analyses', k, analyses, ANALYSIS_FILTERS[k]))}
        </Section>
        <Section title={t('staff.strip.section.reports')}>
          {(Object.keys(REPORT_FILTERS) as ReportFilter[]).map((k) => chip('reports', k, reports, REPORT_FILTERS[k]))}
        </Section>
        {projectId && onCost && (
          <Section title={to('cost.title')}>
            <CostChip projectId={projectId} onPick={onCost} />
          </Section>
        )}
      </Group>
    </Card>
  );
}
